import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { hapticSuccess } from '../native';
import { 
  GitBranch, Users, ArrowLeft, Loader2, Save, RefreshCw,
  ChevronRight, Trophy, Shuffle, Check, X, GripVertical,
  Plus, Minus, Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bracket Match Component
const BracketMatch = ({ 
  match, 
  round, 
  position,
  teams,
  onTeamSelect,
  onScoreUpdate,
  isEditing 
}) => {
  const [localScores, setLocalScores] = useState({
    team1: match.team1_score || '',
    team2: match.team2_score || ''
  });

  const team1 = teams.find(t => t.id === match.team1_id);
  const team2 = teams.find(t => t.id === match.team2_id);

  const handleScoreChange = (team, value) => {
    const score = value === '' ? '' : parseInt(value) || 0;
    setLocalScores(prev => ({ ...prev, [team]: score }));
  };

  const handleScoreSave = () => {
    if (localScores.team1 !== '' && localScores.team2 !== '') {
      onScoreUpdate(match.id, localScores.team1, localScores.team2);
    }
  };

  return (
    <div 
      className={`relative bg-card border rounded-lg p-3 min-w-[200px] transition-all ${
        match.status === 'completed' ? 'border-green-500/50' : 'border-border hover:border-primary/50'
      }`}
      data-testid={`bracket-match-${round}-${position}`}
    >
      {/* Team 1 */}
      <div className={`flex items-center justify-between py-2 ${
        match.winner_id === match.team1_id ? 'text-green-500 font-bold' : ''
      }`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isEditing && !match.team1_id ? (
            <Select onValueChange={(value) => onTeamSelect(match.id, 'team1', value)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                {teams.filter(t => t.id !== match.team2_id).map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="truncate text-sm">{team1?.name || 'TBD'}</span>
          )}
          {match.team1_seed && (
            <Badge variant="outline" className="text-xs h-5">
              #{match.team1_seed}
            </Badge>
          )}
        </div>
        {match.status === 'completed' ? (
          <span className="font-mono font-bold ml-2">{match.team1_score}</span>
        ) : isEditing ? (
          <Input
            type="number"
            min="0"
            value={localScores.team1}
            onChange={(e) => handleScoreChange('team1', e.target.value)}
            className="w-12 h-7 text-center text-xs p-1"
          />
        ) : null}
      </div>

      <div className="border-t my-1" />

      {/* Team 2 */}
      <div className={`flex items-center justify-between py-2 ${
        match.winner_id === match.team2_id ? 'text-green-500 font-bold' : ''
      }`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isEditing && !match.team2_id ? (
            <Select onValueChange={(value) => onTeamSelect(match.id, 'team2', value)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                {teams.filter(t => t.id !== match.team1_id).map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="truncate text-sm">{team2?.name || 'TBD'}</span>
          )}
          {match.team2_seed && (
            <Badge variant="outline" className="text-xs h-5">
              #{match.team2_seed}
            </Badge>
          )}
        </div>
        {match.status === 'completed' ? (
          <span className="font-mono font-bold ml-2">{match.team2_score}</span>
        ) : isEditing ? (
          <Input
            type="number"
            min="0"
            value={localScores.team2}
            onChange={(e) => handleScoreChange('team2', e.target.value)}
            className="w-12 h-7 text-center text-xs p-1"
          />
        ) : null}
      </div>

      {/* Save Score Button */}
      {isEditing && localScores.team1 !== '' && localScores.team2 !== '' && (
        <Button 
          size="sm" 
          className="w-full mt-2 h-7 text-xs"
          onClick={handleScoreSave}
        >
          <Check className="w-3 h-3 mr-1" />
          Save Score
        </Button>
      )}

      {/* Match Status Badge */}
      {match.status === 'completed' && (
        <Badge className="absolute -top-2 -right-2 text-xs bg-green-500">
          Final
        </Badge>
      )}
    </div>
  );
};

// Bracket Round Column
const BracketRound = ({ 
  round, 
  roundNumber, 
  totalRounds, 
  teams, 
  onTeamSelect, 
  onScoreUpdate,
  isEditing 
}) => {
  const roundNames = {
    1: totalRounds === 1 ? 'Final' : totalRounds === 2 ? 'Semifinals' : 'Round 1',
    2: totalRounds === 2 ? 'Final' : totalRounds === 3 ? 'Semifinals' : 'Round 2',
    3: totalRounds === 3 ? 'Final' : totalRounds === 4 ? 'Semifinals' : 'Quarterfinals',
    4: totalRounds === 4 ? 'Final' : 'Semifinals',
    5: 'Final'
  };

  const getRoundName = () => {
    const reverseRound = totalRounds - roundNumber + 1;
    if (reverseRound === totalRounds) return 'Final';
    if (reverseRound === totalRounds - 1) return 'Semifinals';
    if (reverseRound === totalRounds - 2) return 'Quarterfinals';
    return `Round ${roundNumber}`;
  };

  // Calculate spacing for connector lines
  const matchHeight = 90;
  const matchGap = Math.pow(2, roundNumber - 1) * matchHeight;

  return (
    <div className="flex flex-col gap-4 relative">
      <h3 className="text-sm font-semibold text-center text-muted-foreground">
        {getRoundName()}
      </h3>
      <div 
        className="flex flex-col justify-around relative"
        style={{ 
          minHeight: `${Math.pow(2, totalRounds - roundNumber) * 100}px`,
          gap: `${matchGap}px`
        }}
      >
        {round.map((match, idx) => (
          <div key={match.id} className="relative">
            <BracketMatch
              match={match}
              round={roundNumber}
              position={idx}
              teams={teams}
              onTeamSelect={onTeamSelect}
              onScoreUpdate={onScoreUpdate}
              isEditing={isEditing}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// SVG Connector Lines Component
const BracketConnectors = ({ bracket, totalRounds }) => {
  if (bracket.length < 2) return null;
  
  const matchWidth = 220;
  const roundGap = 60;
  const matchHeight = 90;
  
  const lines = [];
  
  for (let roundIdx = 0; roundIdx < bracket.length - 1; roundIdx++) {
    const currentRound = bracket[roundIdx];
    const matchesInRound = currentRound.length;
    const spacing = Math.pow(2, roundIdx) * matchHeight;
    const nextSpacing = Math.pow(2, roundIdx + 1) * matchHeight;
    
    for (let matchIdx = 0; matchIdx < matchesInRound; matchIdx += 2) {
      const x1 = (roundIdx + 1) * (matchWidth + roundGap) - roundGap / 2;
      const y1Top = matchIdx * spacing + spacing / 2 + 45;
      const y1Bottom = (matchIdx + 1) * spacing + spacing / 2 + 45;
      const x2 = x1 + roundGap;
      const yMid = (y1Top + y1Bottom) / 2;
      
      // Line from top match to middle
      lines.push(
        <path
          key={`line-${roundIdx}-${matchIdx}-top`}
          d={`M ${x1} ${y1Top} H ${x1 + 20} V ${yMid} H ${x2}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-border"
        />
      );
      
      // Line from bottom match to middle
      lines.push(
        <path
          key={`line-${roundIdx}-${matchIdx}-bottom`}
          d={`M ${x1} ${y1Bottom} H ${x1 + 20} V ${yMid}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-border"
        />
      );
    }
  }
  
  return (
    <svg 
      className="absolute inset-0 pointer-events-none z-0"
      style={{ 
        width: `${bracket.length * (matchWidth + roundGap)}px`,
        height: '100%'
      }}
    >
      {lines}
    </svg>
  );
};

export const BracketBuilder = () => {
  const { tournamentId } = useParams();
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [bracket, setBracket] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [bracketSize, setBracketSize] = useState(8);
  const [seedingMethod, setSeedingMethod] = useState('random');

  useEffect(() => {
    fetchData();
  }, [tournamentId, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [tournamentRes, teamsRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`, { headers }),
        axios.get(`${API}/teams?tournament_id=${tournamentId}`, { headers })
      ]);
      
      setTournament(tournamentRes.data);
      setTeams(teamsRes.data);
      
      // Fetch existing bracket
      try {
        const bracketRes = await axios.get(
          `${API}/brackets/${tournamentId}`,
          { headers }
        );
        if (bracketRes.data?.rounds) {
          setBracket(bracketRes.data.rounds);
        }
      } catch (e) {
        // No existing bracket
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tournament data');
    } finally {
      setLoading(false);
    }
  };

  const generateBracket = async () => {
    try {
      setSaving(true);
      
      // Calculate number of rounds
      const numRounds = Math.ceil(Math.log2(bracketSize));
      const newBracket = [];
      
      // Get teams to seed
      let seededTeams = [...teams];
      if (seedingMethod === 'random') {
        seededTeams = seededTeams.sort(() => Math.random() - 0.5);
      }
      
      // Create first round matches
      const firstRoundMatches = [];
      for (let i = 0; i < bracketSize / 2; i++) {
        const team1 = seededTeams[i * 2];
        const team2 = seededTeams[i * 2 + 1];
        firstRoundMatches.push({
          id: `match-1-${i}`,
          round: 1,
          position: i,
          team1_id: team1?.id || null,
          team2_id: team2?.id || null,
          team1_seed: i * 2 + 1,
          team2_seed: i * 2 + 2,
          team1_score: null,
          team2_score: null,
          winner_id: null,
          status: 'scheduled'
        });
      }
      newBracket.push(firstRoundMatches);
      
      // Create subsequent rounds
      let prevRoundMatches = firstRoundMatches.length;
      for (let round = 2; round <= numRounds; round++) {
        const roundMatches = [];
        const numMatches = prevRoundMatches / 2;
        for (let i = 0; i < numMatches; i++) {
          roundMatches.push({
            id: `match-${round}-${i}`,
            round,
            position: i,
            team1_id: null,
            team2_id: null,
            team1_score: null,
            team2_score: null,
            winner_id: null,
            status: 'pending'
          });
        }
        newBracket.push(roundMatches);
        prevRoundMatches = numMatches;
      }
      
      setBracket(newBracket);
      setShowGenerateDialog(false);
      toast.success(`Generated ${bracketSize}-team bracket`);
    } catch (error) {
      toast.error('Failed to generate bracket');
    } finally {
      setSaving(false);
    }
  };

  const handleTeamSelect = (matchId, slot, teamId) => {
    setBracket(prev => {
      const newBracket = prev.map(round => 
        round.map(match => {
          if (match.id === matchId) {
            return {
              ...match,
              [slot === 'team1' ? 'team1_id' : 'team2_id']: teamId
            };
          }
          return match;
        })
      );
      return newBracket;
    });
  };

  const handleScoreUpdate = async (matchId, team1Score, team2Score) => {
    const winnerId = team1Score > team2Score 
      ? bracket.flat().find(m => m.id === matchId)?.team1_id
      : bracket.flat().find(m => m.id === matchId)?.team2_id;

    setBracket(prev => {
      const newBracket = prev.map((round, roundIdx) => 
        round.map((match, matchIdx) => {
          if (match.id === matchId) {
            return {
              ...match,
              team1_score: team1Score,
              team2_score: team2Score,
              winner_id: winnerId,
              status: 'completed'
            };
          }
          // Auto-advance winner to next round
          if (roundIdx > 0) {
            const prevRound = prev[roundIdx - 1];
            const sourceMatch = prevRound.find(m => m.id === matchId);
            if (sourceMatch) {
              const sourceIdx = prevRound.indexOf(sourceMatch);
              const targetIdx = Math.floor(sourceIdx / 2);
              const isTopSlot = sourceIdx % 2 === 0;
              
              if (matchIdx === targetIdx) {
                return {
                  ...match,
                  [isTopSlot ? 'team1_id' : 'team2_id']: winnerId
                };
              }
            }
          }
          return match;
        })
      );
      return newBracket;
    });

    hapticSuccess();
    toast.success('Score updated');
  };

  const saveBracket = async () => {
    try {
      setSaving(true);
      await axios.post(
        `${API}/brackets/${tournamentId}`,
        { rounds: bracket },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bracket saved successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to save bracket');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-full mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to={`/tournaments/${tournamentId}`}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <GitBranch className="w-6 h-6" />
                {t('tournament.bracket')} Builder
              </h1>
              <p className="text-muted-foreground">
                {tournament?.name}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {bracket.length === 0 ? (
              <Button onClick={() => setShowGenerateDialog(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Generate Bracket
              </Button>
            ) : (
              <>
                <Button
                  variant={isEditing ? "default" : "outline"}
                  onClick={() => setIsEditing(!isEditing)}
                  className="gap-2"
                >
                  <Settings className="w-4 h-4" />
                  {isEditing ? 'Done Editing' : 'Edit Bracket'}
                </Button>
                <Button
                  onClick={saveBracket}
                  disabled={saving}
                  className="gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Bracket Display */}
        {bracket.length === 0 ? (
          <Card className="p-12 text-center">
            <GitBranch className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Bracket Created Yet</h3>
            <p className="text-muted-foreground mb-6">
              Generate a bracket to start assigning teams and tracking results
            </p>
            <Button onClick={() => setShowGenerateDialog(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Generate Bracket
            </Button>
          </Card>
        ) : (
          <div className="overflow-x-auto pb-6">
            <div className="relative flex gap-16 min-w-max p-4">
              {/* SVG Connector Lines */}
              <BracketConnectors bracket={bracket} totalRounds={bracket.length} />
              
              {/* Bracket Rounds */}
              {bracket.map((round, idx) => (
                <BracketRound
                  key={idx}
                  round={round}
                  roundNumber={idx + 1}
                  totalRounds={bracket.length}
                  teams={teams}
                  onTeamSelect={handleTeamSelect}
                  onScoreUpdate={handleScoreUpdate}
                  isEditing={isEditing}
                />
              ))}
              
              {/* Champion Display */}
              {bracket.length > 0 && bracket[bracket.length - 1][0]?.winner_id && (
                <div className="flex flex-col justify-center">
                  <Card className="p-4 bg-yellow-500/10 border-yellow-500">
                    <div className="text-center">
                      <Trophy className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">Champion</p>
                      <p className="font-bold">
                        {teams.find(t => t.id === bracket[bracket.length - 1][0].winner_id)?.name}
                      </p>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generate Bracket Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Tournament Bracket</DialogTitle>
              <DialogDescription>
                Choose the bracket size and seeding method
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Bracket Size</Label>
                <Select value={bracketSize.toString()} onValueChange={(v) => setBracketSize(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4 Teams</SelectItem>
                    <SelectItem value="8">8 Teams</SelectItem>
                    <SelectItem value="16">16 Teams</SelectItem>
                    <SelectItem value="32">32 Teams</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Seeding Method</Label>
                <Select value={seedingMethod} onValueChange={setSeedingMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    <SelectItem value="manual">Manual (assign later)</SelectItem>
                    <SelectItem value="pool_results">Based on Pool Play Results</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-sm text-muted-foreground">
                {teams.length} teams registered. 
                {teams.length < bracketSize && (
                  <span className="text-yellow-600">
                    {' '}({bracketSize - teams.length} byes will be created)
                  </span>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={generateBracket} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Shuffle className="w-4 h-4 mr-2" />
                )}
                Generate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BracketBuilder;
